export const SYSTEM_TEST_PREFIX = '___TEST_AUTOMATION_COMPANY___';
export const SYSTEM_TEST_OWNER = 'trustwork-system-tests';
const LEGACY_LOGINS = ['test_admin', 'test_admin2'];
export function createSystemTestIdentity() {
  const runId = crypto.randomUUID();
  return { runId, password: crypto.randomUUID(), name: `${SYSTEM_TEST_PREFIX}${runId}`, loginPrefix: `tw_test_${runId.replace(/-/g, '')}`,
    marker: { owner: SYSTEM_TEST_OWNER, run_id: runId, started_at: new Date().toISOString() } };
}
export function isOwnedTestCompany(company) {
  const marker = company.config?._test_automation;
  return marker?.owner === SYSTEM_TEST_OWNER && typeof marker.run_id === 'string' && company.name === `${SYSTEM_TEST_PREFIX}${marker.run_id}`;
}
export async function cleanupSystemTestFixtures(client, { runId, trackedIds = [], staleOnly = false, now = Date.now() } = {}) {
  const candidates = [];
  for (let start = 0; ; start += 500) {
    const { data, error } = await client.from('companies').select('id,name,login_username,config').like('name', `${SYSTEM_TEST_PREFIX}%`).range(start, start + 499);
    if (error) throw new Error(`Cannot inspect test residues: ${error.message}`);
    candidates.push(...(data || []));
    if ((data || []).length < 500) break;
  }
  const ids = new Set(trackedIds);
  for (const company of candidates) {
    if (isOwnedTestCompany(company)) {
      const marker = company.config._test_automation;
      const ownRun = marker.run_id === runId;
      const stale = Number.isFinite(Date.parse(marker.started_at)) && now - Date.parse(marker.started_at) > 3600000;
      if (ownRun || (!runId && (!staleOnly || stale))) ids.add(company.id);
    } else if (!runId && company.name === SYSTEM_TEST_PREFIX && LEGACY_LOGINS.includes(company.login_username)) {
      // Old versions had no run marker. Require both the exact sentinel and known fixture employee codes.
      const { data, error } = await client.from('employees').select('employee_code').eq('company_id', company.id);
      if (error) throw new Error(`Cannot inspect legacy test employees: ${error.message}`);
      if ((data || []).every(emp => ['T-01', 'T-02', 'T-VPF', 'T-MATH'].includes(emp.employee_code))) ids.add(company.id);
    }
  }
  // A tracked ID was returned by this run's insert, so orphaned child rows can be cleaned even if the company was already deleted.
  for (const id of ids) {
    let known = candidates.find(company => company.id === id);
    if (!known) {
      const { data, error } = await client.from('companies').select('id,name,login_username,config').eq('id', id);
      if (error) throw new Error(`Cannot verify cleanup ownership: ${error.message}`);
      known = data?.[0];
    }
    if (known && !(isOwnedTestCompany(known) && (!runId || known.config._test_automation.run_id === runId)) && !(known.name === SYSTEM_TEST_PREFIX && LEGACY_LOGINS.includes(known.login_username))) {
      throw new Error(`Cleanup refused: company ${id} does not match this test run.`);
    }
    for (const table of ['salary_records', 'employees', 'companies']) {
      const { error } = await client.from(table).delete().eq(table === 'companies' ? 'id' : 'company_id', id);
      if (error) throw new Error(`Cleanup failed for ${table}, company ${id}: ${error.message}`);
    }
    for (const table of ['salary_records', 'employees', 'companies']) {
      const { data, error } = await client.from(table).select('id').eq(table === 'companies' ? 'id' : 'company_id', id);
      if (error) throw new Error(`Cleanup verification failed for ${table}: ${error.message}`);
      if (data?.length) throw new Error(`Cleanup incomplete: ${table} still contains test data for company ${id}.`);
    }
  }
  return { removed: ids.size, verified: true };
}
